<template>
  <div class="all-players-stats">
    <div class="filter-controls">
      <div class="filter-group">
        <select id="player-filter" v-model="selectedPlayerNumber" class="player-select">
          <option :value="null">Team</option>
          <option v-for="player in sortedRosterPlayers" :key="player.number" :value="player.number">
            {{ player.number }} - {{ player.name }}
          </option>
        </select>
      </div>

      <div class="column-chips" ref="columnChips">
        <button
          class="btn tab-btn"
          :class="{ active: selectedColumnGroup === 'All' }"
          @click="selectedColumnGroup = 'All'"
        >
          All
        </button>
        <button
          v-for="group in columnGroups"
          :key="group"
          class="btn tab-btn"
          :class="{ active: selectedColumnGroup === group }"
          @click="selectedColumnGroup = group"
        >
          {{ group }}
        </button>
      </div>
    </div>

    <table class="stats-table" ref="statsTable">
      <thead>
        <tr>
          <th class="player-number-cell sortable" @click="setSort('playerNumber')">#</th>
          <th
            class="sticky-col player-name-cell sortable"
            :class="{ 'is-stuck': isSticky }"
            :style="isSticky && stickyColWidth ? { width: `${stickyColWidth}px`, maxWidth: `${stickyColWidth}px` } : null"
            @click="setSort('playerName')"
          >Player</th>
          <th class="col-spacer"></th>
          <th class="sortable" @click="setSort('participation.sets')">Sets</th>
          <th class="sortable sets-border" @click="setSort('participation.points')">Points</th>
          <th class="col-spacer"></th>
          <th v-if="isColumnGroupVisible('Serve')" colspan="14" class="serve-border">Serve</th>
          <th v-if="selectedColumnGroup === 'All'" class="col-spacer"></th>
          <th v-if="isColumnGroupVisible('Receive')" colspan="10" class="receive-border">Receive</th>
          <th v-if="selectedColumnGroup === 'All'" class="col-spacer"></th>
          <th v-if="isColumnGroupVisible('Scoring')" colspan="3" class="scoring-border">Scoring</th>
          <th v-if="selectedColumnGroup === 'All'" class="col-spacer"></th>
          <th v-if="isColumnGroupVisible('Attack')" colspan="16" class="attack-border">Attack</th>
          <th v-if="selectedColumnGroup === 'All'" class="col-spacer"></th>
          <th v-if="isColumnGroupVisible('Block')" colspan="3" class="block-border">Block</th>
          <th v-if="selectedColumnGroup === 'All'" class="col-spacer"></th>
          <th v-if="isColumnGroupVisible('Digs')" colspan="1">Digs</th>
          <th v-if="selectedColumnGroup === 'All'" class="col-spacer"></th>
          <th v-if="isColumnGroupVisible('Errors')" colspan="1">Errors</th>
        </tr>
        <tr>
          <th class="player-number-cell"></th>
          <th
            class="sticky-col player-name-cell"
            :class="{ 'is-stuck': isSticky }"
            :style="isSticky && stickyColWidth ? { width: `${stickyColWidth}px`, maxWidth: `${stickyColWidth}px` } : null"
          ></th>
          <th class="col-spacer"></th>
          <th></th>
          <th class="sets-border"></th>
          <th class="col-spacer"></th>
          <template v-if="isColumnGroupVisible('Serve')">
            <th class="sortable" @click="setSort('serve.aces')">Aces</th>
            <th class="sortable" @click="setSort('serve.acesPercent')">%</th>
            <th class="sortable" @click="setSort('serve.threes')">#3</th>
            <th class="sortable" @click="setSort('serve.threesPercent')">%</th>
            <th class="sortable" @click="setSort('serve.twos')">#2</th>
            <th class="sortable" @click="setSort('serve.twosPercent')">%</th>
            <th class="sortable" @click="setSort('serve.ones')">#1</th>
            <th class="sortable" @click="setSort('serve.onesPercent')">%</th>
            <th class="sortable" @click="setSort('serve.errors')">Errors</th>
            <th class="sortable" @click="setSort('serve.errorsPercent')">%</th>
            <th class="sortable" @click="setSort('serve.pointsOnServe')">Points</th>
            <th class="sortable" @click="setSort('serve.pointsOnServePercent')">%</th>
            <th class="sortable" @click="setSort('serve.totalServes')">Total</th>
            <th class="sortable serve-border" @click="setSort('serve.rating')">Rating</th>
          </template>
          <th v-if="selectedColumnGroup === 'All'" class="col-spacer"></th>
          <template v-if="isColumnGroupVisible('Receive')">
            <th class="sortable" @click="setSort('receive.threes')">#3</th>
            <th class="sortable" @click="setSort('receive.threesPercent')">%</th>
            <th class="sortable" @click="setSort('receive.twos')">#2</th>
            <th class="sortable" @click="setSort('receive.twosPercent')">%</th>
            <th class="sortable" @click="setSort('receive.ones')">#1</th>
            <th class="sortable" @click="setSort('receive.onesPercent')">%</th>
            <th class="sortable" @click="setSort('receive.errors')">Errors</th>
            <th class="sortable" @click="setSort('receive.errorsPercent')">%</th>
            <th class="sortable" @click="setSort('receive.passAttempts')">Attempts</th>
            <th class="sortable receive-border" @click="setSort('receive.rating')">Rating</th>
          </template>
          <th v-if="selectedColumnGroup === 'All'" class="col-spacer"></th>
          <template v-if="isColumnGroupVisible('Scoring')">
            <th class="sortable" @click="setSort('scoring.pointsWon')">Won</th>
            <th class="sortable" @click="setSort('scoring.pointsLost')">Lost</th>
            <th class="sortable scoring-border" @click="setSort('scoring.pointDiff')">Diff</th>
          </template>
          <th v-if="selectedColumnGroup === 'All'" class="col-spacer"></th>
          <template v-if="isColumnGroupVisible('Attack')">
            <th class="sortable" @click="setSort('attack.kills')">Kills</th>
            <th class="sortable" @click="setSort('attack.killsPercent')">%</th>
            <th class="sortable" @click="setSort('attack.errors')">Errors</th>
            <th class="sortable" @click="setSort('attack.errorsPercent')">%</th>
            <th class="sortable" @click="setSort('attack.attempts')">Attempts</th>
            <th class="sortable" @click="setSort('attack.efficiency')">Eff.</th>
            <th class="sortable" @click="setSort('attack.killSubtypes.attack')">K: Atk</th>
            <th class="sortable" @click="setSort('attack.killSubtypes.tip')">K: Tip</th>
            <th class="sortable" @click="setSort('attack.killSubtypes.roll')">K: Roll</th>
            <th class="sortable" @click="setSort('attack.killSubtypes.volley')">K: Vlly</th>
            <th class="sortable" @click="setSort('attack.killSubtypes.dump')">K: Dump</th>
            <th class="sortable" @click="setSort('attack.killSubtypes.other')">K: Other</th>
            <th class="sortable" @click="setSort('attack.errorSubtypes.net')">E: Net</th>
            <th class="sortable" @click="setSort('attack.errorSubtypes.out')">E: Out</th>
            <th class="sortable" @click="setSort('attack.errorSubtypes.block')">E: Blk</th>
            <th class="sortable attack-border" @click="setSort('attack.errorSubtypes.other')">E: Other</th>
          </template>
          <th v-if="selectedColumnGroup === 'All'" class="col-spacer"></th>
          <template v-if="isColumnGroupVisible('Block')">
            <th class="sortable" @click="setSort('block.killBlocks')">Kill</th>
            <th class="sortable" @click="setSort('block.successfulBlocks')">Succ.</th>
            <th class="sortable block-border" @click="setSort('block.blockErrors')">Errors</th>
          </template>
          <th v-if="selectedColumnGroup === 'All'" class="col-spacer"></th>
          <template v-if="isColumnGroupVisible('Digs')">
            <th class="sortable" @click="setSort('digs')">Total</th>
          </template>
          <th v-if="selectedColumnGroup === 'All'" class="col-spacer"></th>
          <template v-if="isColumnGroupVisible('Errors')">
            <th class="sortable" @click="setSort('otherErrors')">Other Err</th>
          </template>
        </tr>
      </thead>
      <tbody v-grey-zero>
        <tr
          v-for="row in sortedRows"
          :key="row.key"
          :class="{
            'team-average-row': row.isTeamAverage,
            'highlighted-row': (row.isTeamAverage && selectedPlayerNumber === null) || (!row.isTeamAverage && selectedPlayerNumber === row.number),
            'dimmed-row': !row.isTeamAverage && selectedPlayerNumber !== null && selectedPlayerNumber !== row.number,
            'row-non-participant': !row.isTeamAverage && nonParticipantNumbers.has(row.number)
          }"
          @click="row.isTeamAverage ? (selectedPlayerNumber = null) : (selectedPlayerNumber = selectedPlayerNumber === row.number ? null : row.number)"
        >
          <td class="player-number-cell">
            <span class="player-number">{{ row.isTeamAverage ? '' : row.number }}</span>
          </td>
          <td
            class="sticky-col player-name-cell"
            :class="{ 'is-stuck': isSticky }"
            :style="isSticky && stickyColWidth ? { width: `${stickyColWidth}px`, maxWidth: `${stickyColWidth}px` } : null"
          >
            <span class="player-name">{{ row.name }}</span>
          </td>
          <td class="col-spacer"></td>
          <td>{{ row.participation.sets }}</td>
          <td class="sets-border">{{ row.participation.points }}</td>
          <td class="col-spacer"></td>
          <template v-if="isColumnGroupVisible('Serve')">
            <td><span :class="{ 'badge-green': row.serve.aces > 0 }">{{ row.serve.aces }}</span></td>
            <td class="percent-cell">{{ row.serve.acesPercent }}</td>
            <td>{{ row.serve.threes }}</td>
            <td class="percent-cell">{{ row.serve.threesPercent }}</td>
            <td>{{ row.serve.twos }}</td>
            <td class="percent-cell">{{ row.serve.twosPercent }}</td>
            <td>{{ row.serve.ones }}</td>
            <td class="percent-cell">{{ row.serve.onesPercent }}</td>
            <td><span :class="{ 'badge-red': row.serve.errors > 0 }">{{ row.serve.errors }}</span></td>
            <td class="percent-cell">{{ row.serve.errorsPercent }}</td>
            <td>{{ row.serve.pointsOnServe }}</td>
            <td class="percent-cell">{{ row.serve.pointsOnServePercent }}</td>
            <td>{{ row.serve.totalServes }}</td>
            <td class="serve-border">{{ row.serve.rating }}</td>
          </template>
          <td v-if="selectedColumnGroup === 'All'" class="col-spacer"></td>
          <template v-if="isColumnGroupVisible('Receive')">
            <td>{{ row.receive.threes }}</td>
            <td class="percent-cell">{{ row.receive.threesPercent }}</td>
            <td>{{ row.receive.twos }}</td>
            <td class="percent-cell">{{ row.receive.twosPercent }}</td>
            <td>{{ row.receive.ones }}</td>
            <td class="percent-cell">{{ row.receive.onesPercent }}</td>
            <td><span :class="{ 'badge-red': row.receive.errors > 0 }">{{ row.receive.errors }}</span></td>
            <td class="percent-cell">{{ row.receive.errorsPercent }}</td>
            <td>{{ row.receive.passAttempts }}</td>
            <td class="receive-border">{{ row.receive.rating }}</td>
          </template>
          <td v-if="selectedColumnGroup === 'All'" class="col-spacer"></td>
          <template v-if="isColumnGroupVisible('Scoring')">
            <td>{{ row.scoring.pointsWon }}</td>
            <td>{{ row.scoring.pointsLost }}</td>
            <td class="scoring-border">{{ row.scoring.pointDiff }}</td>
          </template>
          <td v-if="selectedColumnGroup === 'All'" class="col-spacer"></td>
          <template v-if="isColumnGroupVisible('Attack')">
            <td><span :class="{ 'badge-green': row.attack.kills > 0 }">{{ row.attack.kills }}</span></td>
            <td class="percent-cell">{{ row.attack.killsPercent }}</td>
            <td><span :class="{ 'badge-red': row.attack.errors > 0 }">{{ row.attack.errors }}</span></td>
            <td class="percent-cell">{{ row.attack.errorsPercent }}</td>
            <td>{{ row.attack.attempts }}</td>
            <td>{{ row.attack.efficiency }}</td>
            <td>{{ row.attack.killSubtypes.attack }}</td>
            <td>{{ row.attack.killSubtypes.tip }}</td>
            <td>{{ row.attack.killSubtypes.roll }}</td>
            <td>{{ row.attack.killSubtypes.volley }}</td>
            <td>{{ row.attack.killSubtypes.dump }}</td>
            <td>{{ row.attack.killSubtypes.other }}</td>
            <td>{{ row.attack.errorSubtypes.net }}</td>
            <td>{{ row.attack.errorSubtypes.out }}</td>
            <td>{{ row.attack.errorSubtypes.block }}</td>
            <td class="attack-border">{{ row.attack.errorSubtypes.other }}</td>
          </template>
          <td v-if="selectedColumnGroup === 'All'" class="col-spacer"></td>
          <template v-if="isColumnGroupVisible('Block')">
            <td><span :class="{ 'badge-green': row.block.killBlocks > 0 }">{{ row.block.killBlocks }}</span></td>
            <td>{{ row.block.successfulBlocks }}</td>
            <td class="block-border"><span :class="{ 'badge-red': row.block.blockErrors > 0 }">{{ row.block.blockErrors }}</span></td>
          </template>
          <td v-if="selectedColumnGroup === 'All'" class="col-spacer"></td>
          <template v-if="isColumnGroupVisible('Digs')">
            <td>{{ row.digs }}</td>
          </template>
          <td v-if="selectedColumnGroup === 'All'" class="col-spacer"></td>
          <template v-if="isColumnGroupVisible('Errors')">
            <td><span :class="{ 'badge-red': row.otherErrors > 0 }">{{ row.otherErrors }}</span></td>
          </template>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import { store } from '../store.js';
import teamPlayers from '../team.js';

function markZeroCells(el) {
  el.querySelectorAll('td:not(.col-spacer):not(.player-number-cell):not(.player-name-cell)').forEach(td => {
    const text = td.textContent.trim()
    const num = Number(text.endsWith('%') ? text.slice(0, -1) : text)
    td.classList.toggle('cell-zero', text !== '' && num === 0)
  })
}

export default {
  name: 'AllPlayersStatsTable',
  emits: ['update:selectedPlayerNumber'],
  directives: {
    greyZero: {
      mounted: markZeroCells,
      updated: markZeroCells
    }
  },
  props: {
    points: {
      type: Array,
      default: () => []
    },
    roster: {
      type: Array,
      default: () => []
    },
    sets: {
      type: Array,
      default: () => []
    },
    activeSetId: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      sortKey: 'playerNumber',
      sortDir: 'asc',
      selectedPlayerNumber: null,
      columnGroups: ['Serve', 'Receive', 'Scoring', 'Attack', 'Block', 'Digs', 'Errors'],
      selectedColumnGroup: 'All',
      isSticky: false,
      stickyColWidth: null,
      scrollHandler: null
    };
  },
  watch: {
    selectedPlayerNumber(val) {
      this.$emit('update:selectedPlayerNumber', val);
    },
    selectedColumnGroup() {
      this.$nextTick(() => {
        this.resetTableScroll();
        this.centerActiveChip();
      });
    }
  },
  mounted() {
    this.observeSticky();
  },
  beforeUnmount() {
    const table = this.$el?.querySelector('.stats-table');
    if (table && this.scrollHandler) {
      table.removeEventListener('scroll', this.scrollHandler);
    }
  },
  computed: {
    activeSet() {
      if (!this.activeSetId || this.activeSetId === 'all' || this.activeSetId === 'total') return null;
      return (this.sets || []).find(s => s.id === this.activeSetId) || null;
    },
    nonParticipantNumbers() {
      const set = this.activeSet;
      if (!set) return new Set();
      const lineup = Array.isArray(set.startingLineup) ? set.startingLineup : [];
      const participating = new Set();
      lineup.forEach(item => { if (item.player?.number != null) participating.add(item.player.number); });
      if (set.startingLibero?.number != null) participating.add(set.startingLibero.number);
      (set.points || []).forEach(p => (p.currentLineupNumbers || []).forEach(n => participating.add(n)));
      const roster = Array.isArray(this.roster) ? this.roster : [];
      return new Set(roster.map(p => p.number).filter(n => !participating.has(n)));
    },
    isColumnGroupVisible() {
      return (group) => {
        return this.selectedColumnGroup === 'All' || this.selectedColumnGroup === group;
      };
    },
    sortedRosterPlayers() {
      const roster = Array.isArray(this.roster) ? this.roster : [];
      const unique = new Map();
      roster.forEach(player => {
        if (!player || player.number === undefined) {
          return;
        }
        if (!unique.has(player.number)) {
          unique.set(player.number, player);
        }
      });
      return Array.from(unique.values()).sort((a, b) => a.number - b.number);
    },
    playerRows() {
      return this.sortedRosterPlayers.map(player => this.buildRow(player));
    },
    teamRow() {
      if (!this.playerRows.length) {
        return null;
      }
      return this.buildTeamRow();
    },
    sortedRows() {
      let rows = this.playerRows.slice();


      if (this.teamRow) {
        rows.push(this.teamRow);
      }

      const dir = this.sortDir === 'asc' ? 1 : -1;
      rows.sort((a, b) => {
        const aVal = this.getSortValue(a, this.sortKey);
        const bVal = this.getSortValue(b, this.sortKey);
        if (aVal === bVal) {
          return (Number(a.number) || 0) - (Number(b.number) || 0);
        }
        if (aVal > bVal) {
          return dir;
        }
        return -dir;
      });
      return rows;
    }
  },
  methods: {
    setSort(key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
        return;
      }
      this.sortKey = key;
      this.sortDir = 'asc';
    },
    getSortValue(row, key) {
      if (key === 'playerNumber') {
        return Number(row.number) || 0;
      }
      if (key === 'playerName') {
        return (row.name || '').toLowerCase();
      }
      const value = key.split('.').reduce((acc, part) => (acc ? acc[part] : undefined), row);
      if (typeof value === 'string' && value.endsWith('%')) {
        return Number(value.replace('%', '')) || 0;
      }
      const numeric = Number(value);
      return Number.isNaN(numeric) ? 0 : numeric;
    },
    buildRow(player) {
      const number = player.number;
      const participation = this.getParticipationStats(number);
      const serve = this.getServeStats(number);
      const receive = this.getReceiveStats(number);
      const attack = this.getAttackStats(number);
      const block = this.getBlockStats(number);
      const digs = this.getDigs(number);
      const scoring = this.getScoringStats(number, serve, receive, attack, block);
      const otherErrors = this.getOtherErrorsForPoints(this.points, number);
      return {
        key: String(number),
        number,
        name: player.name || '',
        participation,
        serve,
        receive,
        scoring,
        attack,
        block,
        digs,
        otherErrors,
        isTeamAverage: false
      };
    },
    buildTeamRow() {
      const points = Array.isArray(this.points) ? this.points : [];
      const teamPoints = points.length;
      const teamSets = (this.activeSetId && this.activeSetId !== 'total' && this.activeSetId !== 'all')
        ? 1
        : (Array.isArray(this.sets) ? this.sets.length : 0);

      const servePoints = points.filter(point =>
        point.startPoint === 'Serve' && !point.serveAction.call
      );
      const totalServes = servePoints.length;
      const countServeByRating = rating => servePoints.filter(point => point.serveAction.rating === rating).length;
      const serveAces = servePoints.filter(
        point => point.serveAction.rating === 4 || point.scoreAction.pointDetail === 'Ace'
      ).length;
      const serveThrees = countServeByRating(3);
      const serveTwos = countServeByRating(2);
      const serveOnes = countServeByRating(1);
      const serveErrors = countServeByRating(0);
      const pointsOnServe = servePoints.filter(point => point.scoreAction.outcome === 'Won').length;
      const serveRating = totalServes
        ? ((serveAces * 4 + serveThrees * 3 + serveTwos * 2 + serveOnes * 1 + pointsOnServe * 0.5) / totalServes).toFixed(2)
        : '0.00';

      const receivePoints = points.filter(point => point.startPoint === 'Receive');
      const passAttempts = receivePoints.filter(point => point.receiveAction.rating !== null).length;
      const countReceiveByRating = rating => receivePoints.filter(point => point.receiveAction.rating === rating).length;
      const receiveThrees = countReceiveByRating(3);
      const receiveTwos = countReceiveByRating(2);
      const receiveOnes = countReceiveByRating(1);
      const receiveErrors = countReceiveByRating(0);
      const receiveRating = passAttempts
        ? ((receiveThrees * 3 + receiveTwos * 2 + receiveOnes * 1) / passAttempts).toFixed(2)
        : '0.00';

      const killPoints = points.filter(
        point => point.scoreAction.outcome === 'Won' && point.scoreAction.pointDetail === 'Kill'
      );
      const kills = killPoints.length;
      const killSubtypeNames = ['attack', 'tip', 'roll', 'volley', 'dump', 'other'];
      const killSubtypes = Object.fromEntries(
        killSubtypeNames.map(sub => [sub, killPoints.filter(p => p.scoreAction.attackSubtype === sub).length])
      );
      const attackErrorPoints = points.filter(
        point => point.scoreAction.outcome === 'Lost' && ['Attack error', 'Opp. kill block'].includes(point.scoreAction.pointDetail)
      );
      const attackErrors = attackErrorPoints.length;
      const errorSubtypeNames = ['net', 'out', 'block', 'other'];
      const errorSubtypes = Object.fromEntries(
        errorSubtypeNames.map(sub => [sub, attackErrorPoints.filter(p => p.scoreAction.attackErrorSubtype === sub).length])
      );
      const rallyAttacks = points.reduce((total, point) => {
        const bucket = point.duringRally?.attacks || {};
        return total + Object.values(bucket).reduce((sum, value) => sum + value, 0);
      }, 0);
      const attackAttempts = kills + attackErrors + rallyAttacks;
      const attackEfficiency = attackAttempts
        ? `${(((kills - attackErrors) / attackAttempts) * 100).toFixed(1)}%`
        : '0.0%';

      const killBlocksSolo = points.filter(
        point => point.scoreAction.outcome === 'Won' && point.scoreAction.pointDetail === 'Kill block (solo)'
      ).length;
      const killBlocksJoint = points.filter(
        point => point.scoreAction.outcome === 'Won' && point.scoreAction.pointDetail === 'Kill block (assist)'
      ).length;
      const killBlocks = killBlocksSolo + killBlocksJoint;
      const nonTerminalSolo = points.reduce((total, point) => {
        const bucket = point.duringRally?.blocksSolo || {};
        return total + Object.values(bucket).reduce((sum, value) => sum + value, 0);
      }, 0);
      const nonTerminalJoint = points.reduce((total, point) => {
        const bucket = point.duringRally?.blocksJoint || {};
        return total + Object.values(bucket).reduce((sum, value) => sum + value, 0) * 0.5;
      }, 0);
      const successfulBlocks = killBlocks + nonTerminalSolo + nonTerminalJoint;
      const blockErrors = points.filter(
        point => point.scoreAction.outcome === 'Lost' && point.scoreAction.pointDetail === 'Block error'
      ).length;

      const otherErrors = this.getTotalOtherErrors(points);
      const pointsWon = kills + killBlocks + serveAces;
      const pointsLost = attackErrors + blockErrors + receiveErrors + serveErrors + otherErrors;

      const digs = points.reduce((total, point) => {
        const bucket = point.duringRally?.digs || {};
        return total + Object.values(bucket).reduce((sum, value) => sum + value, 0);
      }, 0);

      return {
        key: 'team',
        number: '',
        name: 'Team',
        participation: {
          sets: teamSets,
          points: teamPoints
        },
        serve: {
          aces: serveAces,
          acesPercent: totalServes ? `${((serveAces / totalServes) * 100).toFixed(1)}%` : '0.0%',
          threes: serveThrees,
          threesPercent: totalServes ? `${((serveThrees / totalServes) * 100).toFixed(1)}%` : '0.0%',
          twos: serveTwos,
          twosPercent: totalServes ? `${((serveTwos / totalServes) * 100).toFixed(1)}%` : '0.0%',
          ones: serveOnes,
          onesPercent: totalServes ? `${((serveOnes / totalServes) * 100).toFixed(1)}%` : '0.0%',
          errors: serveErrors,
          errorsPercent: totalServes ? `${((serveErrors / totalServes) * 100).toFixed(1)}%` : '0.0%',
          pointsOnServe,
          pointsOnServePercent: totalServes ? `${((pointsOnServe / totalServes) * 100).toFixed(1)}%` : '0.0%',
          totalServes,
          rating: serveRating
        },
        receive: {
          threes: receiveThrees,
          threesPercent: passAttempts ? `${((receiveThrees / passAttempts) * 100).toFixed(1)}%` : '0.0%',
          twos: receiveTwos,
          twosPercent: passAttempts ? `${((receiveTwos / passAttempts) * 100).toFixed(1)}%` : '0.0%',
          ones: receiveOnes,
          onesPercent: passAttempts ? `${((receiveOnes / passAttempts) * 100).toFixed(1)}%` : '0.0%',
          errors: receiveErrors,
          errorsPercent: passAttempts ? `${((receiveErrors / passAttempts) * 100).toFixed(1)}%` : '0.0%',
          passAttempts,
          rating: receiveRating
        },
        scoring: {
          pointsWon,
          pointsLost,
          pointDiff: pointsWon - pointsLost
        },
        attack: {
          kills,
          killsPercent: attackAttempts ? `${((kills / attackAttempts) * 100).toFixed(1)}%` : '0.0%',
          killSubtypes,
          errors: attackErrors,
          errorsPercent: attackAttempts ? `${((attackErrors / attackAttempts) * 100).toFixed(1)}%` : '0.0%',
          errorSubtypes,
          attempts: attackAttempts,
          efficiency: attackEfficiency
        },
        block: {
          killBlocks,
          successfulBlocks,
          blockErrors
        },
        digs,
        otherErrors,
        isTeamAverage: true
      };
    },
    getParticipationStats(number) {
      const pointsPlayed = this.points.filter(point => this.isOnCourt(point, number)).length;
      if (this.activeSetId && this.activeSetId !== 'total' && this.activeSetId !== 'all') {
        return {
          sets: pointsPlayed > 0 ? 1 : 0,
          points: pointsPlayed
        };
      }
      const sets = Array.isArray(this.sets) ? this.sets : [];
      const setsPlayed = sets.reduce((total, set) => {
        const points = Array.isArray(set.points) ? set.points : [];
        const played = points.some(point => this.isOnCourt(point, number));
        return total + (played ? 1 : 0);
      }, 0);
      return {
        sets: setsPlayed,
        points: pointsPlayed
      };
    },
    isOnCourt(point, number) {
      const onCourt = point && Array.isArray(point.currentOnCourtNumbers)
        ? point.currentOnCourtNumbers
        : [];
      return onCourt.includes(number);
    },
    getScoringStats(number, serve, receive, attack, block) {
      const otherErrors = this.points.filter(
        point =>
          point.scoreAction.outcome === 'Lost' &&
          (point.scoreAction.pointDetail === 'Other error' || point.scoreAction.pointDetail === 'Rotation Error') &&
          ((point.scoreAction.player && point.scoreAction.player.number === number) ||
            (point.scoreAction.secondPlayer && point.scoreAction.secondPlayer.number === number))
      ).length;
      const pointsWon = attack.kills + block.killBlocks + serve.aces;
      const pointsLost = attack.errors + block.blockErrors + receive.errors + serve.errors + otherErrors;
      return {
        pointsWon,
        pointsLost,
        pointDiff: pointsWon - pointsLost
      };
    },
    getServeStats(number) {
      const servePoints = this.points.filter(point =>
        point.startPoint === 'Serve' &&
        !point.serveAction.call &&
        point.serveAction.player &&
        point.serveAction.player.number === number
      );
      const totalServes = servePoints.length;
      const countByRating = rating => servePoints.filter(point => point.serveAction.rating === rating).length;
      const aces = servePoints.filter(point => point.serveAction.rating === 4 || point.scoreAction.pointDetail === 'Ace').length;
      const threes = countByRating(3);
      const twos = countByRating(2);
      const ones = countByRating(1);
      const errors = countByRating(0);
      const pointsOnServe = servePoints.filter(point => point.scoreAction.outcome === 'Won').length;
      const rating = totalServes
        ? ((aces * 4 + threes * 3 + twos * 2 + ones * 1 + pointsOnServe * 0.5) / totalServes).toFixed(2)
        : '0.00';

      return {
        aces,
        acesPercent: totalServes ? `${((aces / totalServes) * 100).toFixed(1)}%` : '0.0%',
        threes,
        threesPercent: totalServes ? `${((threes / totalServes) * 100).toFixed(1)}%` : '0.0%',
        twos,
        twosPercent: totalServes ? `${((twos / totalServes) * 100).toFixed(1)}%` : '0.0%',
        ones,
        onesPercent: totalServes ? `${((ones / totalServes) * 100).toFixed(1)}%` : '0.0%',
        errors,
        errorsPercent: totalServes ? `${((errors / totalServes) * 100).toFixed(1)}%` : '0.0%',
        pointsOnServe,
        pointsOnServePercent: totalServes ? `${((pointsOnServe / totalServes) * 100).toFixed(1)}%` : '0.0%',
        totalServes,
        rating
      };
    },
    getReceiveStats(number) {
      const receivePoints = this.points.filter(point =>
        point.startPoint === 'Receive' &&
        point.receiveAction.player &&
        point.receiveAction.player.number === number
      );
      const passAttempts = receivePoints.filter(point => point.receiveAction.rating !== null).length;
      const countByRating = rating => receivePoints.filter(point => point.receiveAction.rating === rating).length;
      const threes = countByRating(3);
      const twos = countByRating(2);
      const ones = countByRating(1);
      const errors = countByRating(0);
      const rating = passAttempts
        ? ((threes * 3 + twos * 2 + ones * 1) / passAttempts).toFixed(2)
        : '0.00';

      return {
        threes,
        threesPercent: passAttempts ? `${((threes / passAttempts) * 100).toFixed(1)}%` : '0.0%',
        twos,
        twosPercent: passAttempts ? `${((twos / passAttempts) * 100).toFixed(1)}%` : '0.0%',
        ones,
        onesPercent: passAttempts ? `${((ones / passAttempts) * 100).toFixed(1)}%` : '0.0%',
        errors,
        errorsPercent: passAttempts ? `${((errors / passAttempts) * 100).toFixed(1)}%` : '0.0%',
        passAttempts,
        rating
      };
    },
    getAttackStats(number) {
      const hasSelected = point => {
        const primary = point.scoreAction.player && point.scoreAction.player.number;
        const secondary = point.scoreAction.secondPlayer && point.scoreAction.secondPlayer.number;
        return [primary, secondary].includes(number);
      };
      const killPoints = this.points.filter(
        point =>
          point.scoreAction.outcome === 'Won' &&
          point.scoreAction.pointDetail === 'Kill' &&
          hasSelected(point)
      );
      const kills = killPoints.length;
      const killSubtypeNames = ['attack', 'tip', 'roll', 'volley', 'dump', 'other'];
      const killSubtypes = Object.fromEntries(
        killSubtypeNames.map(sub => [sub, killPoints.filter(p => p.scoreAction.attackSubtype === sub).length])
      );
      const attackErrorPoints = this.points.filter(
        point =>
          point.scoreAction.outcome === 'Lost' &&
          ['Attack error', 'Opp. kill block'].includes(point.scoreAction.pointDetail) &&
          hasSelected(point)
      );
      const errors = attackErrorPoints.length;
      const errorSubtypeNames = ['net', 'out', 'block', 'other'];
      const errorSubtypes = Object.fromEntries(
        errorSubtypeNames.map(sub => [sub, attackErrorPoints.filter(p => p.scoreAction.attackErrorSubtype === sub).length])
      );
      const rallyAttacks = this.points.reduce((total, point) => {
        const bucket = point.duringRally?.attacks || {};
        return total + (bucket[number] || 0);
      }, 0);
      const attempts = kills + errors + rallyAttacks;
      const efficiency = attempts
        ? `${(((kills - errors) / attempts) * 100).toFixed(1)}%`
        : '0.0%';

      return {
        kills,
        killsPercent: attempts ? `${((kills / attempts) * 100).toFixed(1)}%` : '0.0%',
        killSubtypes,
        errors,
        errorsPercent: attempts ? `${((errors / attempts) * 100).toFixed(1)}%` : '0.0%',
        errorSubtypes,
        attempts,
        efficiency
      };
    },
    getBlockStats(number) {
      const killBlocksSolo = this.points.filter(
        point =>
          point.scoreAction.outcome === 'Won' &&
          point.scoreAction.pointDetail === 'Kill block (solo)' &&
          point.scoreAction.player &&
          point.scoreAction.player.number === number
      ).length;
      const killBlocksJoint = this.points.reduce((total, point) => {
        if (
          point.scoreAction.outcome !== 'Won' ||
          point.scoreAction.pointDetail !== 'Kill block (assist)'
        ) {
          return total;
        }
        const primary = point.scoreAction.player && point.scoreAction.player.number;
        const secondary = point.scoreAction.secondPlayer && point.scoreAction.secondPlayer.number;
        if (primary === number) {
          total += 0.5;
        }
        if (secondary === number) {
          total += 0.5;
        }
        return total;
      }, 0);
      const killBlocks = killBlocksSolo + killBlocksJoint;
      const nonTerminalSolo = this.points.reduce((total, point) => {
        const bucket = point.duringRally?.blocksSolo || {};
        return total + (bucket[number] || 0);
      }, 0);
      const nonTerminalJoint = this.points.reduce((total, point) => {
        const bucket = point.duringRally?.blocksJoint || {};
        return total + (bucket[number] || 0) * 0.5;
      }, 0);
      const successfulBlocks = killBlocks + nonTerminalSolo + nonTerminalJoint;
      const blockErrors = this.points.filter(
        point =>
          point.scoreAction.outcome === 'Lost' &&
          point.scoreAction.pointDetail === 'Block error' &&
          point.scoreAction.player &&
          point.scoreAction.player.number === number
      ).length;

      return {
        killBlocks,
        successfulBlocks,
        blockErrors
      };
    },
    getDigs(number) {
      return this.points.reduce((total, point) => {
        const bucket = point.duringRally?.digs || {};
        return total + (bucket[number] || 0);
      }, 0);
    },
    generateMatchId() {
      const date = this.getMatchMetadata().date;
      const opp = this.getMatchMetadata().opposition.replace(/\s+/g, '_');
      const timestamp = new Date().getTime();
      return `${date}_vs_${opp}_${timestamp}`;
    },
    getMatchMetadata() {
      const metadata = (store && store.match && store.match.metadata) || {};
      const sets = Array.isArray(this.sets) ? this.sets : [];
      return {
        teamName: metadata.teamName || 'Moss Side Jaguars',
        opposition: metadata.opposition || 'Opposition',
        date: metadata.date || new Date().toISOString().split('T')[0],
        homeAway: metadata.homeAway || 'Home',
        competition: metadata.competition || '',
        round: metadata.round || '',
        venue: metadata.venue || '',
        finalScore: {
          setsWon: (store && store.match && typeof store.match.setsWon === 'number') ? store.match.setsWon : 0,
          setsLost: (store && store.match && typeof store.match.setsLost === 'number') ? store.match.setsLost : 0
        },
        setScores: sets.map((set, index) => ({
          setNumber: index + 1,
          score: {
            won: (set.score && typeof set.score.won === 'number') ? set.score.won : 0,
            lost: (set.score && typeof set.score.lost === 'number') ? set.score.lost : 0
          }
        }))
      };
    },
    getRosterInfo() {
      const roster = Array.isArray(this.roster) ? this.roster : [];
      const teamPlayerById = new Map(teamPlayers.map(p => [p.id, p]));
      const unique = new Map();
      roster.forEach(player => {
        if (!player || player.number === undefined) {
          return;
        }
        if (!unique.has(player.number)) {
          const teamPlayer = teamPlayerById.get(player.id) || {};
          // Split name if firstName/lastName not available
          let firstName = player.firstName || '';
          let lastName = player.lastName || '';

          if (!firstName && !lastName && player.name) {
            const nameParts = player.name.trim().split(/\s+/);
            if (nameParts.length === 1) {
              lastName = nameParts[0];
            } else {
              firstName = nameParts[0];
              lastName = nameParts.slice(1).join(' ');
            }
          }

          unique.set(player.number, {
            number: player.number,
            name: player.name || `Player ${player.number}`,
            firstName,
            lastName,
            position: teamPlayer.position || (player.playerType === 'libero' ? 'libero' : '')
          });
        }
      });
      return Array.from(unique.values()).sort((a, b) => a.number - b.number);
    },
    buildMatchTotalStats() {
      const allPoints = this.getAllMatchPoints();
      const teamSets = Array.isArray(this.sets) ? this.sets.length : 0;
      const teamStats = this.buildTeamStatsForPoints(allPoints, { includeSets: true, setsCount: teamSets });
      const oppositionStats = this.buildOppositionStatsForPoints(allPoints, { includeSets: true, setsCount: teamSets });
      const playerStats = this.buildPlayerStatsForPoints(allPoints, {
        includeSets: true,
        participationLookup: (number) => this.getMatchParticipationStats(number, allPoints)
      });

      return {
        team: {
          key: 'team',
          number: '',
          name: 'Team',
          isTeamAverage: true,
          ...teamStats
        },
        opposition: {
          key: 'opposition',
          number: '',
          name: 'Opposition',
          isTeamAverage: true,
          ...oppositionStats
        },
        players: playerStats
      };
    },
    buildSetBreakdown() {
      const sets = Array.isArray(this.sets) ? this.sets : [];
      return sets.map((set, index) => {
        const setPoints = Array.isArray(set.points) ? set.points : [];
        const teamStats = this.buildTeamStatsForPoints(setPoints);
        const oppositionStats = this.buildOppositionStatsForPoints(setPoints);

        return {
          setNumber: index + 1,
          team: { key: 'team', name: 'Team', isTeamAverage: true, ...teamStats },
          opposition: { key: 'opposition', name: 'Opposition', isTeamAverage: true, ...oppositionStats },
          players: this.buildPlayerStatsForPoints(setPoints, { set, stripNonParticipantStats: true }),
          points: setPoints.map(p => this.leanPoint(p))
        };
      });
    },
    leanPoint(point) {
      const leanPlayer = (p) => (p && p.number != null) ? { number: p.number } : null;
      return {
        startPoint: point.startPoint,
        serveAction: point.serveAction ? {
          player: leanPlayer(point.serveAction.player),
          rating: point.serveAction.rating,
          ...(point.serveAction.call ? { call: point.serveAction.call } : {})
        } : null,
        receiveAction: point.receiveAction ? {
          player: leanPlayer(point.receiveAction.player),
          rating: point.receiveAction.rating,
          ...(point.receiveAction.call ? { call: point.receiveAction.call } : {})
        } : null,
        scoreAction: point.scoreAction ? {
          outcome: point.scoreAction.outcome,
          pointDetail: point.scoreAction.pointDetail,
          player: leanPlayer(point.scoreAction.player),
          secondPlayer: leanPlayer(point.scoreAction.secondPlayer),
          ...(point.scoreAction.attackSubtype ? { attackSubtype: point.scoreAction.attackSubtype } : {}),
          ...(point.scoreAction.attackErrorSubtype ? { attackErrorSubtype: point.scoreAction.attackErrorSubtype } : {})
        } : null,
        currentScore: point.currentScore,
        currentRotationCode: point.currentRotationCode,
        currentLineupNumbers: point.currentLineupNumbers,
        currentOnCourtNumbers: point.currentOnCourtNumbers,
        duringRallyEvents: point.duringRallyEvents
      };
    },
    buildTeamStatsForPoints(points, options = {}) {
      const { includeSets = false, setsCount = 0 } = options;
      const teamPoints = points.length;

      const servePoints = points.filter(point =>
        point.startPoint === 'Serve' && !point.serveAction.call
      );
      const totalServes = servePoints.length;
      const countServeByRating = rating => servePoints.filter(point => point.serveAction.rating === rating).length;
      const serveAces = servePoints.filter(
        point => point.serveAction.rating === 4 || point.scoreAction.pointDetail === 'Ace'
      ).length;
      const serveThrees = countServeByRating(3);
      const serveTwos = countServeByRating(2);
      const serveOnes = countServeByRating(1);
      const serveErrors = countServeByRating(0);
      const pointsOnServe = servePoints.filter(point => point.scoreAction.outcome === 'Won').length;
      const serveRating = totalServes
        ? ((serveAces * 4 + serveThrees * 3 + serveTwos * 2 + serveOnes * 1 + pointsOnServe * 0.5) / totalServes).toFixed(2)
        : '0.00';

      const receivePoints = points.filter(point => point.startPoint === 'Receive');
      const passAttempts = receivePoints.filter(point => point.receiveAction.rating !== null).length;
      const countReceiveByRating = rating => receivePoints.filter(point => point.receiveAction.rating === rating).length;
      const receiveThrees = countReceiveByRating(3);
      const receiveTwos = countReceiveByRating(2);
      const receiveOnes = countReceiveByRating(1);
      const receiveErrors = countReceiveByRating(0);
      const receiveRating = passAttempts
        ? ((receiveThrees * 3 + receiveTwos * 2 + receiveOnes * 1) / passAttempts).toFixed(2)
        : '0.00';

      const killPoints = points.filter(
        point => point.scoreAction.outcome === 'Won' && point.scoreAction.pointDetail === 'Kill'
      );
      const kills = killPoints.length;
      const killSubtypeNames = ['attack', 'tip', 'roll', 'volley', 'dump', 'other'];
      const killSubtypes = Object.fromEntries(
        killSubtypeNames.map(sub => [sub, killPoints.filter(p => p.scoreAction.attackSubtype === sub).length])
      );
      const attackErrorPoints = points.filter(
        point => point.scoreAction.outcome === 'Lost' && ['Attack error', 'Opp. kill block'].includes(point.scoreAction.pointDetail)
      );
      const attackErrors = attackErrorPoints.length;
      const errorSubtypeNames = ['net', 'out', 'block', 'other'];
      const errorSubtypes = Object.fromEntries(
        errorSubtypeNames.map(sub => [sub, attackErrorPoints.filter(p => p.scoreAction.attackErrorSubtype === sub).length])
      );
      const rallyAttacks = points.reduce((total, point) => {
        const bucket = point.duringRally?.attacks || {};
        return total + Object.values(bucket).reduce((sum, value) => sum + value, 0);
      }, 0);
      const attackAttempts = kills + attackErrors + rallyAttacks;
      const attackEfficiency = attackAttempts
        ? `${(((kills - attackErrors) / attackAttempts) * 100).toFixed(1)}%`
        : '0.0%';

      const killBlocksSolo = points.filter(
        point => point.scoreAction.outcome === 'Won' && point.scoreAction.pointDetail === 'Kill block (solo)'
      ).length;
      const killBlocksJoint = points.filter(
        point => point.scoreAction.outcome === 'Won' && point.scoreAction.pointDetail === 'Kill block (assist)'
      ).length;
      const killBlocks = killBlocksSolo + killBlocksJoint;
      const nonTerminalSolo = points.reduce((total, point) => {
        const bucket = point.duringRally?.blocksSolo || {};
        return total + Object.values(bucket).reduce((sum, value) => sum + value, 0);
      }, 0);
      const nonTerminalJoint = points.reduce((total, point) => {
        const bucket = point.duringRally?.blocksJoint || {};
        return total + Object.values(bucket).reduce((sum, value) => sum + value, 0) * 0.5;
      }, 0);
      const successfulBlocks = killBlocks + nonTerminalSolo + nonTerminalJoint;
      const blockErrors = points.filter(
        point => point.scoreAction.outcome === 'Lost' && point.scoreAction.pointDetail === 'Block error'
      ).length;

      const otherErrors = this.getTotalOtherErrors(points);
      const pointsWon = kills + killBlocks + serveAces;
      const pointsLost = attackErrors + blockErrors + receiveErrors + serveErrors + otherErrors;

      const digs = points.reduce((total, point) => {
        const bucket = point.duringRally?.digs || {};
        return total + Object.values(bucket).reduce((sum, value) => sum + value, 0);
      }, 0);

      const participation = includeSets
        ? { sets: setsCount, points: teamPoints }
        : { points: teamPoints };

      return {
        participation,
        serve: {
          aces: serveAces,
          acesPercent: totalServes ? `${((serveAces / totalServes) * 100).toFixed(1)}%` : '0.0%',
          threes: serveThrees,
          threesPercent: totalServes ? `${((serveThrees / totalServes) * 100).toFixed(1)}%` : '0.0%',
          twos: serveTwos,
          twosPercent: totalServes ? `${((serveTwos / totalServes) * 100).toFixed(1)}%` : '0.0%',
          ones: serveOnes,
          onesPercent: totalServes ? `${((serveOnes / totalServes) * 100).toFixed(1)}%` : '0.0%',
          errors: serveErrors,
          errorsPercent: totalServes ? `${((serveErrors / totalServes) * 100).toFixed(1)}%` : '0.0%',
          pointsOnServe,
          pointsOnServePercent: totalServes ? `${((pointsOnServe / totalServes) * 100).toFixed(1)}%` : '0.0%',
          totalServes,
          rating: serveRating
        },
        receive: {
          threes: receiveThrees,
          threesPercent: passAttempts ? `${((receiveThrees / passAttempts) * 100).toFixed(1)}%` : '0.0%',
          twos: receiveTwos,
          twosPercent: passAttempts ? `${((receiveTwos / passAttempts) * 100).toFixed(1)}%` : '0.0%',
          ones: receiveOnes,
          onesPercent: passAttempts ? `${((receiveOnes / passAttempts) * 100).toFixed(1)}%` : '0.0%',
          errors: receiveErrors,
          errorsPercent: passAttempts ? `${((receiveErrors / passAttempts) * 100).toFixed(1)}%` : '0.0%',
          passAttempts,
          rating: receiveRating
        },
        scoring: {
          pointsWon,
          pointsLost,
          pointDiff: pointsWon - pointsLost
        },
        attack: {
          kills,
          killsPercent: attackAttempts ? `${((kills / attackAttempts) * 100).toFixed(1)}%` : '0.0%',
          killSubtypes,
          errors: attackErrors,
          errorsPercent: attackAttempts ? `${((attackErrors / attackAttempts) * 100).toFixed(1)}%` : '0.0%',
          errorSubtypes,
          attempts: attackAttempts,
          efficiency: attackEfficiency
        },
        block: {
          killBlocks,
          successfulBlocks,
          blockErrors
        },
        digs,
        otherErrors,
        isTeamAverage: true
      };
    },
    buildPlayerStatsForPoints(points, options = {}) {
      const { includeSets = false, participationLookup = null, set = null } = options;
      const roster = Array.isArray(this.roster) ? this.roster : [];
      const unique = new Map();
      roster.forEach(player => {
        if (!player || player.number === undefined) {
          return;
        }
        if (!unique.has(player.number)) {
          unique.set(player.number, player);
        }
      });
      const sorted = Array.from(unique.values()).sort((a, b) => a.number - b.number);

      // Build role lookup from set startingLineup + startingLibero if available,
      // then fill in substitutes from their inherited .role on the roster object.
      const roleToCode = { setter: 'S', opposite: 'OP', outside: 'OH', middle: 'MB', libero: 'L' };
      const roleByNumber = new Map();
      if (set) {
        const lineup = Array.isArray(set.startingLineup) ? set.startingLineup : [];
        lineup.forEach(item => {
          if (item.player && item.player.number !== undefined) {
            roleByNumber.set(item.player.number, {
              roleCode: item.roleCode || '',
              role: item.player.role || '',
              rotationPosition: item.rotationPosition,
              isLibero: false
            });
          }
        });
        if (set.startingLibero && set.startingLibero.number !== undefined) {
          const libero = set.startingLibero;
          roleByNumber.set(libero.number, {
            roleCode: 'L',
            role: libero.role || 'libero',
            rotationPosition: null,
            isLibero: true
          });
        }
        // Substitutes: any player who appeared in this set's currentLineupNumbers
        // but isn't already in the role map — their .role was inherited from the
        // player they replaced (set by handlePlayerSubstitution).
        const setPoints = Array.isArray(set.points) ? set.points : [];
        setPoints.forEach(point => {
          const lineupNumbers = point.currentLineupNumbers || [];
          lineupNumbers.forEach(number => {
            if (number !== undefined && !roleByNumber.has(number)) {
              const rosterPlayer = unique.get(number);
              if (rosterPlayer && rosterPlayer.role) {
                roleByNumber.set(number, {
                  roleCode: roleToCode[rosterPlayer.role] || '',
                  role: rosterPlayer.role,
                  rotationPosition: null,
                  isLibero: false
                });
              }
            }
          });
        });
      }

      return sorted.map(player => {
        const number = player.number;
        const participation = includeSets
          ? (participationLookup ? participationLookup(number) : this.getMatchParticipationStats(number, points))
          : this.getParticipationStatsForPoints(points, number);
        const nominalPosition = player.position || (player.playerType === 'libero' ? 'libero' : '');
        const roleInfo = roleByNumber.get(number) || (nominalPosition ? {
          roleCode: roleToCode[nominalPosition] || '',
          role: nominalPosition,
          rotationPosition: null,
          isLibero: player.playerType === 'libero'
        } : null);

        const isLibero = (roleInfo ? roleInfo.isLibero : false) || player.playerType === 'libero';

        const base = {
          number,
          name: player.name || '',
          ...(roleInfo && {
            roleCode: roleInfo.roleCode,
            role: roleInfo.role,
            ...(roleInfo.rotationPosition != null && { rotationPosition: roleInfo.rotationPosition })
          }),
          participation
        };

        if (options.stripNonParticipantStats && participation.points === 0) {
          return base;
        }

        const receive = this.getReceiveStatsForPoints(points, number);
        const digs = this.getDigsForPoints(points, number);
        const otherErrors = this.getOtherErrorsForPoints(points, number);

        const attack = this.getAttackStatsForPoints(points, number);

        if (isLibero) {
          const scoring = this.getScoringStatsForPoints(points, number, null, receive, attack, null);
          return { ...base, receive, scoring, attack, digs, otherErrors };
        }

        const serve = this.getServeStatsForPoints(points, number);
        const block = this.getBlockStatsForPoints(points, number);
        const scoring = this.getScoringStatsForPoints(points, number, serve, receive, attack, block);
        return {
          ...base,
          serve,
          receive,
          scoring,
          attack,
          block,
          digs,
          otherErrors
        };
      });
    },
    getAllMatchPoints() {
      const sets = Array.isArray(this.sets) ? this.sets : [];
      return sets.flatMap(set => Array.isArray(set.points) ? set.points : []);
    },
    getMatchParticipationStats(number, allPointsOverride = null) {
      const sets = Array.isArray(this.sets) ? this.sets : [];
      const allPoints = allPointsOverride || this.getAllMatchPoints();
      const pointsPlayed = allPoints.filter(point => this.isOnCourt(point, number)).length;
      const setsPlayed = sets.reduce((total, set) => {
        const points = Array.isArray(set.points) ? set.points : [];
        const played = points.some(point => this.isOnCourt(point, number));
        return total + (played ? 1 : 0);
      }, 0);
      return {
        sets: setsPlayed,
        points: pointsPlayed
      };
    },
    getOtherErrorsForPoints(points, number) {
      const targetNumber = Number(number);
      if (!Number.isFinite(targetNumber)) {
        return 0;
      }
      return points.filter(point => {
        if (
          point.scoreAction.outcome !== 'Lost' ||
          (point.scoreAction.pointDetail !== 'Other error' && point.scoreAction.pointDetail !== 'Rotation Error')
        ) {
          return false;
        }
        const primary = Number(point.scoreAction.player?.number);
        const secondary = Number(point.scoreAction.secondPlayer?.number);
        return primary === targetNumber || secondary === targetNumber;
      }).length;
    },
    getTotalOtherErrors(points) {
      return points.reduce((total, point) => {
        if (
          point.scoreAction.outcome !== 'Lost' ||
          (point.scoreAction.pointDetail !== 'Other error' && point.scoreAction.pointDetail !== 'Rotation Error')
        ) {
          return total;
        }
        const primary = Number(point.scoreAction.player?.number);
        const secondary = Number(point.scoreAction.secondPlayer?.number);
        const primaryValid = Number.isFinite(primary);
        const secondaryValid = Number.isFinite(secondary);
        if (!primaryValid && !secondaryValid) {
          return total + 1;
        }
        let increment = 0;
        if (primaryValid) {
          increment += 1;
        }
        if (secondaryValid && secondary !== primary) {
          increment += 1;
        }
        return total + increment;
      }, 0);
    },
    getOppositionOtherErrors(points) {
      return points.filter(
        point =>
          point.scoreAction.outcome === 'Won' &&
          (point.scoreAction.pointDetail === 'Opp. other error' || point.scoreAction.pointDetail === 'Opposition Rotation Error')
      ).length;
    },
    buildOppositionStatsForPoints(points, options = {}) {
      const { includeSets = false, setsCount = 0 } = options;
      const teamPoints = points.length;

      const receivePoints = points.filter(
        point => point.startPoint === 'Receive' && !point.receiveAction?.call
      );
      const totalServes = receivePoints.length;
      const passErrors = receivePoints.filter(point => point.receiveAction.rating === 0).length;
      const passOnes = receivePoints.filter(point => point.receiveAction.rating === 1).length;
      const passTwos = receivePoints.filter(point => point.receiveAction.rating === 2).length;
      const passThrees = receivePoints.filter(point => point.receiveAction.rating === 3).length;
      const serveAces = receivePoints.filter(
        point => point.receiveAction.rating === 0 || point.scoreAction.pointDetail === 'Opp. ace'
      ).length;
      // Map our pass ratings to opposition serve ratings:
      // our 3-pass => their #1 serve, our 2-pass => their #2 serve,
      // our 1-pass => their #3 serve, our 0-pass => their ace (#4)
      const serveThrees = passOnes;
      const serveTwos = passTwos;
      const serveOnes = passThrees;
      const serveErrors = receivePoints.filter(
        point => point.scoreAction.pointDetail === 'Opp. serve error'
      ).length;
      const pointsOnServe = receivePoints.filter(point => point.scoreAction.outcome === 'Lost').length;
      const serveRating = totalServes
        ? ((passErrors * 4 + passOnes * 3 + passTwos * 2 + passThrees * 1 + pointsOnServe * 0.5) / totalServes).toFixed(2)
        : '0.00';

      const servePoints = points.filter(point => point.startPoint === 'Serve' && !point.serveAction.call);
      const oppTotalServes = servePoints.length;
      const oppServeErrors = servePoints.filter(point => point.serveAction.rating === 0).length;
      const serveRatingOnes = servePoints.filter(point => point.serveAction.rating === 1).length;
      const serveRatingTwos = servePoints.filter(point => point.serveAction.rating === 2).length;
      const serveRatingThrees = servePoints.filter(point => point.serveAction.rating === 3).length;
      const serveRatingFours = servePoints.filter(
        point => point.serveAction.rating === 4 || point.scoreAction.pointDetail === 'Ace'
      ).length;
      const passAttempts = Math.max(oppTotalServes - oppServeErrors, 0);
      const receiveThrees = serveRatingOnes;
      const receiveTwos = serveRatingTwos;
      const receiveOnes = serveRatingThrees;
      const receiveErrors = serveRatingFours;
      const receiveRating = passAttempts
        ? ((receiveThrees * 3 + receiveTwos * 2 + receiveOnes * 1) / passAttempts).toFixed(2)
        : '0.00';

      const kills = points.filter(
        point => point.scoreAction.outcome === 'Lost' && point.scoreAction.pointDetail === 'Opp. kill'
      ).length;
      const attackErrors = points.filter(
        point =>
          point.scoreAction.outcome === 'Won' &&
          ['Opp. attack error', 'Kill block (solo)', 'Kill block (assist)'].includes(point.scoreAction.pointDetail)
      ).length;
      const rallyDigs = points.reduce((total, point) => {
        const bucket = point.duringRally?.digs || {};
        return total + Object.values(bucket).reduce((sum, value) => sum + value, 0);
      }, 0);
      const attackAttempts = kills + attackErrors + rallyDigs;
      const attackEfficiency = attackAttempts
        ? `${(((kills - attackErrors) / attackAttempts) * 100).toFixed(1)}%`
        : '0.0%';

      const killBlocks = points.filter(
        point => point.scoreAction.outcome === 'Lost' && point.scoreAction.pointDetail === 'Opp. kill block'
      ).length;
      const successfulBlocks = killBlocks;
      const blockErrors = points.filter(
        point => point.scoreAction.outcome === 'Won' && point.scoreAction.pointDetail === 'Opp. block error'
      ).length;

      const otherErrors = this.getOppositionOtherErrors(points);
      const pointsWon = kills + killBlocks + serveAces;
      const pointsLost = attackErrors + blockErrors + receiveErrors + serveErrors + otherErrors;

      const rallyAttacks = points.reduce((total, point) => {
        const bucket = point.duringRally?.attacks || {};
        return total + Object.values(bucket).reduce((sum, value) => sum + value, 0);
      }, 0);
      const nonTerminalBlocks = points.reduce((total, point) => {
        const solo = point.duringRally?.blocksSolo || {};
        const joint = point.duringRally?.blocksJoint || {};
        return total +
          Object.values(solo).reduce((sum, value) => sum + value, 0) +
          Object.values(joint).reduce((sum, value) => sum + value, 0) * 0.5;
      }, 0);
      const digs = rallyAttacks + nonTerminalBlocks;

      const participation = includeSets
        ? { sets: setsCount, points: teamPoints }
        : { points: teamPoints };

      return {
        participation,
        serve: {
          aces: serveAces,
          acesPercent: totalServes ? `${((serveAces / totalServes) * 100).toFixed(1)}%` : '0.0%',
          threes: serveThrees,
          threesPercent: totalServes ? `${((serveThrees / totalServes) * 100).toFixed(1)}%` : '0.0%',
          twos: serveTwos,
          twosPercent: totalServes ? `${((serveTwos / totalServes) * 100).toFixed(1)}%` : '0.0%',
          ones: serveOnes,
          onesPercent: totalServes ? `${((serveOnes / totalServes) * 100).toFixed(1)}%` : '0.0%',
          errors: serveErrors,
          errorsPercent: totalServes ? `${((serveErrors / totalServes) * 100).toFixed(1)}%` : '0.0%',
          pointsOnServe,
          pointsOnServePercent: totalServes ? `${((pointsOnServe / totalServes) * 100).toFixed(1)}%` : '0.0%',
          totalServes,
          rating: serveRating
        },
        receive: {
          threes: receiveThrees,
          threesPercent: passAttempts ? `${((receiveThrees / passAttempts) * 100).toFixed(1)}%` : '0.0%',
          twos: receiveTwos,
          twosPercent: passAttempts ? `${((receiveTwos / passAttempts) * 100).toFixed(1)}%` : '0.0%',
          ones: receiveOnes,
          onesPercent: passAttempts ? `${((receiveOnes / passAttempts) * 100).toFixed(1)}%` : '0.0%',
          errors: receiveErrors,
          errorsPercent: passAttempts ? `${((receiveErrors / passAttempts) * 100).toFixed(1)}%` : '0.0%',
          passAttempts,
          rating: receiveRating
        },
        scoring: {
          pointsWon,
          pointsLost,
          pointDiff: pointsWon - pointsLost
        },
        attack: {
          kills,
          killsPercent: attackAttempts ? `${((kills / attackAttempts) * 100).toFixed(1)}%` : '0.0%',
          errors: attackErrors,
          errorsPercent: attackAttempts ? `${((attackErrors / attackAttempts) * 100).toFixed(1)}%` : '0.0%',
          attempts: attackAttempts,
          efficiency: attackEfficiency
        },
        block: {
          killBlocks,
          successfulBlocks,
          blockErrors
        },
        digs,
        otherErrors
      };
    },
    getParticipationStatsForPoints(points, number) {
      const pointsPlayed = points.filter(point => {
        const onCourt = point && Array.isArray(point.currentOnCourtNumbers)
          ? point.currentOnCourtNumbers
          : [];
        return onCourt.includes(number);
      }).length;
      return { points: pointsPlayed };
    },
    getServeStatsForPoints(points, number) {
      const servePoints = points.filter(point =>
        point.startPoint === 'Serve' &&
        !point.serveAction.call &&
        point.serveAction.player &&
        point.serveAction.player.number === number
      );
      const totalServes = servePoints.length;
      const countByRating = rating => servePoints.filter(point => point.serveAction.rating === rating).length;
      const aces = servePoints.filter(point => point.serveAction.rating === 4 || point.scoreAction.pointDetail === 'Ace').length;
      const threes = countByRating(3);
      const twos = countByRating(2);
      const ones = countByRating(1);
      const errors = countByRating(0);
      const pointsOnServe = servePoints.filter(point => point.scoreAction.outcome === 'Won').length;
      const rating = totalServes
        ? ((aces * 4 + threes * 3 + twos * 2 + ones * 1 + pointsOnServe * 0.5) / totalServes).toFixed(2)
        : '0.00';

      return {
        aces,
        acesPercent: totalServes ? `${((aces / totalServes) * 100).toFixed(1)}%` : '0.0%',
        threes,
        threesPercent: totalServes ? `${((threes / totalServes) * 100).toFixed(1)}%` : '0.0%',
        twos,
        twosPercent: totalServes ? `${((twos / totalServes) * 100).toFixed(1)}%` : '0.0%',
        ones,
        onesPercent: totalServes ? `${((ones / totalServes) * 100).toFixed(1)}%` : '0.0%',
        errors,
        errorsPercent: totalServes ? `${((errors / totalServes) * 100).toFixed(1)}%` : '0.0%',
        pointsOnServe,
        pointsOnServePercent: totalServes ? `${((pointsOnServe / totalServes) * 100).toFixed(1)}%` : '0.0%',
        totalServes,
        rating
      };
    },
    getReceiveStatsForPoints(points, number) {
      const receivePoints = points.filter(point =>
        point.startPoint === 'Receive' &&
        point.receiveAction.player &&
        point.receiveAction.player.number === number
      );
      const passAttempts = receivePoints.filter(point => point.receiveAction.rating !== null).length;
      const countByRating = rating => receivePoints.filter(point => point.receiveAction.rating === rating).length;
      const threes = countByRating(3);
      const twos = countByRating(2);
      const ones = countByRating(1);
      const errors = countByRating(0);
      const rating = passAttempts
        ? ((threes * 3 + twos * 2 + ones * 1) / passAttempts).toFixed(2)
        : '0.00';

      return {
        threes,
        threesPercent: passAttempts ? `${((threes / passAttempts) * 100).toFixed(1)}%` : '0.0%',
        twos,
        twosPercent: passAttempts ? `${((twos / passAttempts) * 100).toFixed(1)}%` : '0.0%',
        ones,
        onesPercent: passAttempts ? `${((ones / passAttempts) * 100).toFixed(1)}%` : '0.0%',
        errors,
        errorsPercent: passAttempts ? `${((errors / passAttempts) * 100).toFixed(1)}%` : '0.0%',
        passAttempts,
        rating
      };
    },
    getAttackStatsForPoints(points, number) {
      const hasSelected = point => {
        const primary = point.scoreAction.player && point.scoreAction.player.number;
        const secondary = point.scoreAction.secondPlayer && point.scoreAction.secondPlayer.number;
        return [primary, secondary].includes(number);
      };
      const killPoints = points.filter(
        point =>
          point.scoreAction.outcome === 'Won' &&
          point.scoreAction.pointDetail === 'Kill' &&
          hasSelected(point)
      );
      const kills = killPoints.length;
      const killSubtypeNames = ['attack', 'tip', 'roll', 'volley', 'dump', 'other'];
      const killSubtypes = Object.fromEntries(
        killSubtypeNames.map(sub => [sub, killPoints.filter(p => p.scoreAction.attackSubtype === sub).length])
      );
      const attackErrorPoints = points.filter(
        point =>
          point.scoreAction.outcome === 'Lost' &&
          ['Attack error', 'Opp. kill block'].includes(point.scoreAction.pointDetail) &&
          hasSelected(point)
      );
      const errors = attackErrorPoints.length;
      const errorSubtypeNames = ['net', 'out', 'block', 'other'];
      const errorSubtypes = Object.fromEntries(
        errorSubtypeNames.map(sub => [sub, attackErrorPoints.filter(p => p.scoreAction.attackErrorSubtype === sub).length])
      );
      const rallyAttacks = points.reduce((total, point) => {
        const bucket = point.duringRally?.attacks || {};
        return total + (bucket[number] || 0);
      }, 0);
      const attempts = kills + errors + rallyAttacks;
      const efficiency = attempts
        ? `${(((kills - errors) / attempts) * 100).toFixed(1)}%`
        : '0.0%';

      return {
        kills,
        killsPercent: attempts ? `${((kills / attempts) * 100).toFixed(1)}%` : '0.0%',
        killSubtypes,
        errors,
        errorsPercent: attempts ? `${((errors / attempts) * 100).toFixed(1)}%` : '0.0%',
        errorSubtypes,
        attempts,
        efficiency
      };
    },
    getBlockStatsForPoints(points, number) {
      const killBlocksSolo = points.filter(
        point =>
          point.scoreAction.outcome === 'Won' &&
          point.scoreAction.pointDetail === 'Kill block (solo)' &&
          point.scoreAction.player &&
          point.scoreAction.player.number === number
      ).length;
      const killBlocksJoint = points.reduce((total, point) => {
        if (
          point.scoreAction.outcome !== 'Won' ||
          point.scoreAction.pointDetail !== 'Kill block (assist)'
        ) {
          return total;
        }
        const primary = point.scoreAction.player && point.scoreAction.player.number;
        const secondary = point.scoreAction.secondPlayer && point.scoreAction.secondPlayer.number;
        if (primary === number) {
          total += 0.5;
        }
        if (secondary === number) {
          total += 0.5;
        }
        return total;
      }, 0);
      const killBlocks = killBlocksSolo + killBlocksJoint;
      const nonTerminalSolo = points.reduce((total, point) => {
        const bucket = point.duringRally?.blocksSolo || {};
        return total + (bucket[number] || 0);
      }, 0);
      const nonTerminalJoint = points.reduce((total, point) => {
        const bucket = point.duringRally?.blocksJoint || {};
        return total + (bucket[number] || 0) * 0.5;
      }, 0);
      const successfulBlocks = killBlocks + nonTerminalSolo + nonTerminalJoint;
      const blockErrors = points.filter(
        point =>
          point.scoreAction.outcome === 'Lost' &&
          point.scoreAction.pointDetail === 'Block error' &&
          point.scoreAction.player &&
          point.scoreAction.player.number === number
      ).length;

      return {
        killBlocks,
        successfulBlocks,
        blockErrors
      };
    },
    getDigsForPoints(points, number) {
      return points.reduce((total, point) => {
        const bucket = point.duringRally?.digs || {};
        return total + (bucket[number] || 0);
      }, 0);
    },
    getScoringStatsForPoints(points, number, serve, receive, attack, block) {
      const otherErrors = points.filter(
        point =>
          point.scoreAction.outcome === 'Lost' &&
          (point.scoreAction.pointDetail === 'Other error' || point.scoreAction.pointDetail === 'Rotation Error') &&
          ((point.scoreAction.player && point.scoreAction.player.number === number) ||
            (point.scoreAction.secondPlayer && point.scoreAction.secondPlayer.number === number))
      ).length;
      const pointsWon = (attack?.kills || 0) + (block?.killBlocks || 0) + (serve?.aces || 0);
      const pointsLost = (attack?.errors || 0) + (block?.blockErrors || 0) + (receive?.errors || 0) + (serve?.errors || 0) + otherErrors;
      return {
        pointsWon,
        pointsLost,
        pointDiff: pointsWon - pointsLost
      };
    },
    generateStatsExport() {
      const matchData = {
        matchId: this.generateMatchId(),
        schemaVersion: "1.0",
        createdAt: (store && store.match && store.match.metadata && store.match.metadata.date) || new Date().toISOString().split('T')[0],
        exportedAt: new Date().toISOString(),
        metadata: this.getMatchMetadata(),
        matchTotal: this.buildMatchTotalStats(),
        sets: this.buildSetBreakdown(),
        roster: this.getRosterInfo()
      };

      return matchData;
    },
    exportMatchStats() {
      try {
        const statsData = this.generateStatsExport();
        const jsonString = JSON.stringify(statsData);
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${statsData.matchId}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        console.log('Match stats exported successfully');
        console.log('File size:', (jsonString.length / 1024).toFixed(2), 'KB');
      } catch (error) {
        console.error('Error exporting match stats:', error);
        alert('Failed to export match stats. Check console for details.');
      }
    },
    resetTableScroll() {
      const table = this.$refs.statsTable || this.$el?.querySelector('.stats-table');
      if (!table) return;
      table.scrollLeft = 0;
      table.scrollTop = 0;
    },
    centerActiveChip() {
      const chips = this.$refs.columnChips;
      if (!chips) return;
      if (chips.scrollWidth <= chips.clientWidth) return;
      const activeChip = chips.querySelector('.chip.active');
      if (!activeChip) return;
      const chipsRect = chips.getBoundingClientRect();
      const chipRect = activeChip.getBoundingClientRect();
      const currentScroll = chips.scrollLeft;
      const chipCenter = (chipRect.left - chipsRect.left) + currentScroll + (chipRect.width / 2);
      const targetScroll = chipCenter - (chipsRect.width / 2);
      const maxScroll = chips.scrollWidth - chips.clientWidth;
      chips.scrollTo({ left: Math.max(0, Math.min(targetScroll, maxScroll)), behavior: 'smooth' });
    },
    observeSticky() {
      const table = this.$el.querySelector('.stats-table');
      if (!table) return;
      this.scrollHandler = () => {
        const numberCell = table.querySelector('.player-number-cell');
        const numberCellWidth = numberCell ? numberCell.offsetWidth : 0;
        const threshold = numberCellWidth + 3;
        if (table.scrollLeft < threshold) {
          this.isSticky = false;
          this.stickyColWidth = null;
        } else {
          this.isSticky = true;
          const startWidth = 120;
          const minWidth = 60;
          const widthRange = startWidth - minWidth;
          const scrollPastThreshold = table.scrollLeft - threshold;
          const widthReduction = Math.min(scrollPastThreshold, widthRange);
          this.stickyColWidth = Math.max(startWidth - widthReduction, minWidth);
        }
      };
      table.addEventListener('scroll', this.scrollHandler);
    }
  }
};
</script>

<style lang="scss">
@use '../table-styles';
</style>

<style scoped lang="scss">
.all-players-stats {
  color: var(--grey-900);
}

.filter-controls {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

// Override shared full-bleed column-chips — doesn't work inside a panel
.column-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  width: auto;
  margin-left: 0;
  padding: 0;
}

.stats-table tbody tr {
  cursor: pointer;
}

.stats-table tbody tr.row-non-participant td {
  color: var(--grey-50) !important;
}

.stats-table tbody tr.row-non-participant.highlighted-row td {
  color: var(--grey-400) !important;
}

.stats-table tbody tr.highlighted-row td {
  background: var(--grey-900) !important;
  color: var(--white);

  &.col-spacer {
    background: var(--grey-5) !important;
  }
}

.stats-table tbody tr.dimmed-row td {
  color: var(--grey-300);
}

.filter-group {

  .player-select {
    height: 34px;
    padding: 0 8px;
    border: 1px solid var(--grey-75);
    border-radius: 8px;
    font-size: 12px;
    font-family: inherit;
    background: var(--white);
    cursor: pointer;
    min-width: 160px;

    &:hover {
      border-color: var(--grey-300);
    }

    &:focus {
      outline: none;
      border-color: var(--red-accent);
    }
  }
}
</style>
